import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { orderBy, parseInt, find } from "lodash";
import moment from "moment";

// components (global)
import { FilterCell } from "components/Table/CustomCell";

// devexpress
import { Getter } from "@devexpress/dx-react-core";
import {
  FilteringState,
  IntegratedFiltering,
  IntegratedPaging,
  IntegratedSorting,
  PagingState,
  SortingState,
  EditingState,
} from "@devexpress/dx-react-grid";
import {
  Grid as GridTable,
  PagingPanel,
  Table,
  TableEditRow,
  TableEditColumn,
  TableFilterRow,
  TableFixedColumns,
  TableHeaderRow,
} from "@devexpress/dx-react-grid-material-ui";

// firebase
import { useFirestore } from "react-redux-firebase";

// material-ui
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from "@material-ui/icons";
import { IconButton, Paper } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

// styles
const useStyles = makeStyles((theme) => ({
  root: { marginTop: theme.spacing(2) },
}));

const MicroEngineDataTable = () => {
  const classes = useStyles();
  const snackbar = useSnackbar();
  const firestore = useFirestore();

  // selector
  const auth = useSelector((state) => state.firebase.auth);
  const profile = useSelector((state) => state.firebase.profile);
  const selectedVendor = useSelector((state) => state.main.selectedVendor);
  const floorAccessSelector = useSelector(
    (state) => state.firestore.ordered.floorAccess ?? []
  );
  const liftAccessSelector = useSelector(
    (state) => state.firestore.data.liftAccess ?? []
  );

  let columns = [
    {
      name: "display_name",
      title: "Display Name",
    },
    {
      name: "name",
      title: "Name",
    },
    {
      name: "description",
      title: "Description",
    },
    {
      name: "floor_group_id",
      title: "Floor Group Id",
    },
    { name: "created_at", title: "Uploaded Date" },
  ];

  const columnExtensions = [
    {
      columnName: "ActionsColumn",
      filteringEnabled: false,
      sortingEnabled: false,
      groupingEnabled: false,
      align: "center",
      width: 200,
    },
  ];

  // memo
  const liftAccess = useMemo(() => {
    let flatResult = [];
    if (liftAccessSelector) {
      flatResult = Object.keys(liftAccessSelector).map((key) => {
        const group = liftAccessSelector[key];
        const assignedBlock = find(floorAccessSelector, (o) => {
          return (
            o.resident?.lift_access?.id === key ||
            o.visitor?.lift_access?.id === key
          );
        });

        return !!group
          ? {
              ...group,
              id: key,
              editable: !!!assignedBlock,
              created_at: moment
                .unix(group.created_at?.seconds)
                .format("DD/MM/YYYY h:mm a"),
            }
          : {};
      });
    }
    return orderBy(flatResult, ["name"], ["asc"]);
  }, [liftAccessSelector, floorAccessSelector]);

  // functions
  const AddButton = ({ onExecute }) => (
    <IconButton onClick={onExecute} size="small" title="Add new row">
      <AddIcon />
    </IconButton>
  );

  const EditButton = ({ onExecute }) => (
    <IconButton onClick={onExecute} size="small" title="Edit row">
      <EditIcon />
    </IconButton>
  );

  const DeleteButton = ({ onExecute }) => (
    <IconButton
      onClick={() => {
        // eslint-disable-next-line
        if (
          window.confirm("Are you sure you want to delete this door group?")
        ) {
          onExecute();
        }
      }}
      title="Delete row"
    >
      <DeleteIcon />
    </IconButton>
  );

  const CommitButton = ({ onExecute }) => (
    <IconButton onClick={onExecute} size="small" title="Save changes">
      <SaveIcon />
    </IconButton>
  );

  const CancelButton = ({ onExecute }) => (
    <IconButton
      color="secondary"
      onClick={onExecute}
      size="small"
      title="Cancel changes"
    >
      <CancelIcon />
    </IconButton>
  );

  const commandComponents = {
    add: AddButton,
    edit: EditButton,
    delete: DeleteButton,
    commit: CommitButton,
    cancel: CancelButton,
  };

  const Command = ({ id, onExecute }) => {
    const CommandButton = commandComponents[id];
    return <CommandButton onExecute={onExecute} />;
  };

  const EditRowCell = (props) => {
    const { column } = props;

    if (column.name !== "created_at") {
      const newProps = {
        ...props,
        editingenabled: props.editingEnabled.toString(),
      };

      delete newProps.editingEnabled;

      return <TableEditRow.Cell {...newProps} />;
    } else {
      const newProps = {
        ...props,
        editingenabled: props.editingEnabled.toString(),
      };

      delete newProps.editingEnabled;
      delete newProps.onValueChange;

      return <Table.Cell {...newProps} />;
    }
  };

  const EditColumnCell = (props) => {
    const { row } = props;
    const newProps = {
      ...props,
    };

    if (!!row.editable !== true && !!row.id) {
      newProps.children = [];
    }

    return <TableEditColumn.Cell {...newProps} />;
  };

  const liftAccessRef = firestore
    .collection("ac_vendors")
    .doc(selectedVendor)
    .collection("lift_access_levels");

  const commitChanges = async ({ added, changed }) => {
    // added
    if (!!added && added.length > 0) {
      const data = added[0];
      await handleAddLiftAccess(data);
    }

    // update
    if (!!changed) {
      const docId = Object.keys(changed)[0];
      const changedValue = changed[docId];

      await handleUpdateLiftAccess(docId, changedValue);
    }
  };

  const handleAddLiftAccess = async (data) => {
    if (Object.keys(data).length === 0) {
      snackbar.enqueueSnackbar(
        "Data not saved. you have submitted error records.",
        {
          variant: "error",
          persist: false,
        }
      );
      return;
    }

    try {
      await liftAccessRef.add({
        created_at: new Date(),
        created_by: {
          user_id: auth.uid,
          name: profile.name,
        },
        ...data,
      });

      snackbar.enqueueSnackbar("New Lift Access had been added", {
        variant: "success",
      });
    } catch (e) {
      snackbar.enqueueSnackbar(e.message, {
        variant: "error",
        persist: false,
      });
    }
  };

  const handleUpdateLiftAccess = async (docId, changedValue) => {
    try {
      await liftAccessRef.doc(docId).update({
        ...changedValue,
      });

      snackbar.enqueueSnackbar("Lift Access had been updated", {
        variant: "success",
      });
    } catch (e) {
      snackbar.enqueueSnackbar(e.message, {
        variant: "error",
        persist: false,
      });
    }
  };

  if (!liftAccess) return null;

  return (
    <div className={classes.root}>
      <Paper>
        <GridTable
          getRowId={(row) => row.id}
          rows={liftAccess}
          columns={columns}
        >
          <FilteringState
            defaultFilters={[]}
            columnExtensions={columnExtensions}
          />
          <IntegratedFiltering />

          <SortingState defaultSorting={[]} />
          <IntegratedSorting />

          <PagingState defaultCurrentPage={0} defaultPageSize={5} />
          <IntegratedPaging />

          <EditingState onCommitChanges={commitChanges} />

          <Table columnExtensions={columnExtensions} />
          <TableHeaderRow showSortingControls={true} />

          <TableFilterRow cellComponent={FilterCell} />

          <TableEditRow cellComponent={EditRowCell} />
          <TableEditColumn
            showAddCommand
            showEditCommand
            commandComponent={Command}
            cellComponent={EditColumnCell}
          />

          <PagingPanel pageSizes={[5, 10, 15, 0]} />

          <Getter
            name="tableColumns"
            computed={({ tableColumns }) => {
              //   debugger;
              const result = [
                ...tableColumns.filter(
                  (c) => c.type !== TableEditColumn.COLUMN_TYPE
                ),
                {
                  key: "editCommand",
                  type: TableEditColumn.COLUMN_TYPE,
                  width: 100,
                },
              ];
              return result;
            }}
          />

          <TableFixedColumns
            leftColumns={["display_name"]}
            rightColumns={[TableEditColumn.COLUMN_TYPE]}
          />
        </GridTable>
      </Paper>
    </div>
  );
};

export default MicroEngineDataTable;
